'use client';

import { useState, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { pomodoroTimerContent } from '../../data/pomodoro-timer-content';

type TimerMode = 'work' | 'break' | 'longBreak';

const WORK_DURATION = 25 * 60; // 25 minutes in seconds
const SHORT_BREAK_DURATION = 5 * 60; // 5 minutes in seconds
const LONG_BREAK_DURATION = 15 * 60; // 15 minutes in seconds
const WORK_SESSIONS_BEFORE_LONG_BREAK = 4;

export default function PomodoroTimer() {
  const [timeLeft, setTimeLeft] = useState<number>(WORK_DURATION);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [mode, setMode] = useState<TimerMode>('work');
  const [workSessionsCompleted, setWorkSessionsCompleted] = useState<number>(0);
  const [totalWorkSessions, setTotalWorkSessions] = useState<number>(0);

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Switch to the next timer mode
  const switchMode = useCallback((nextMode: TimerMode) => {
    setMode(nextMode);
    setIsActive(false);

    switch (nextMode) {
      case 'work':
        setTimeLeft(WORK_DURATION);
        break;
      case 'break':
        setTimeLeft(SHORT_BREAK_DURATION);
        break;
      case 'longBreak':
        setTimeLeft(LONG_BREAK_DURATION);
        break;
    }
  }, []);

  // Handle timer completion
  const handleComplete = useCallback(() => {
    const audio = new Audio('/sounds/bell.mp3');
    audio.play().catch(e => console.log('Audio play failed:', e));

    if (mode === 'work') {
      const newWorkSessions = workSessionsCompleted + 1;
      setWorkSessionsCompleted(newWorkSessions);

      if (newWorkSessions % WORK_SESSIONS_BEFORE_LONG_BREAK === 0) {
        switchMode('longBreak');
      } else {
        switchMode('break');
      }
    } else {
      switchMode('work');
    }
  }, [mode, workSessionsCompleted, switchMode]);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      handleComplete();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, timeLeft, handleComplete]);

  // Toggle timer
  const toggleTimer = () => {
    setIsActive(!isActive);
  };

  // Reset timer
  const resetTimer = () => {
    setIsActive(false);
    if (mode === 'work') {
      setTimeLeft(WORK_DURATION);
    } else if (mode === 'break') {
      setTimeLeft(SHORT_BREAK_DURATION);
    } else {
      setTimeLeft(LONG_BREAK_DURATION);
    }
  };

  // Calculate progress percentage
  const getProgressPercentage = (): number => {
    let totalTime = WORK_DURATION;
    if (mode === 'break') totalTime = SHORT_BREAK_DURATION;
    if (mode === 'longBreak') totalTime = LONG_BREAK_DURATION;
    return ((totalTime - timeLeft) / totalTime) * 100;
  };

  // Get mode title and color
  const getModeDetails = () => {
    switch (mode) {
      case 'work':
        return { title: 'Focus Time', color: 'bg-red-500' };
      case 'break':
        return { title: 'Short Break', color: 'bg-green-500' };
      case 'longBreak':
        return { title: 'Long Break', color: 'bg-blue-500' };
      default:
        return { title: 'Pomodoro Timer', color: 'bg-gray-500' };
    }
  };

  const modeDetails = getModeDetails();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-6 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <ToolPageIntro title="Pomodoro Timer - Free Online Focus Timer">
                Boost productivity with our free online Pomodoro timer. Use the proven Pomodoro Technique to manage time, stay focused, and achieve better work-life balance.
            </ToolPageIntro>
        </div>

        {/* Timer Section - Centered */}
        <div className="flex justify-center mb-12">
          <div className="w-full max-w-md">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
              {/* Progress bar */}
              <div
                className={`h-2 ${modeDetails.color} transition-all duration-1000 ease-linear`}
                style={{ width: `${getProgressPercentage()}%` }}
              />

              {/* Timer display */}
              <div className="p-8 text-center">
                <div className="mb-6">
                  <h2 className={`text-2xl font-semibold ${mode === 'work' ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {modeDetails.title}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Session: {Math.floor(totalWorkSessions / WORK_SESSIONS_BEFORE_LONG_BREAK) + 1}
                  </p>
                </div>

                <div className="text-7xl font-bold mb-8 text-gray-900 dark:text-white">
                  {formatTime(timeLeft)}
                </div>

                {/* Controls */}
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={toggleTimer}
                    className={`flex items-center justify-center w-16 h-16 rounded-full ${isActive ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-500 hover:bg-green-600'
                      } text-white shadow-lg transition-colors`}
                    aria-label={isActive ? 'Pause' : 'Start'}
                  >
                    {isActive ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
                  </button>

                  <button
                    onClick={resetTimer}
                    className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 shadow-lg transition-colors"
                    aria-label="Reset"
                  >
                    <RotateCcw size={24} />
                  </button>
                </div>
              </div>

              {/* Mode selector */}
              <div className="grid grid-cols-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => switchMode('work')}
                  className={`py-4 px-2 text-sm font-medium ${mode === 'work'
                    ? 'text-red-600 dark:text-red-400 border-b-2 border-red-500'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  Work
                </button>
                <button
                  onClick={() => switchMode('break')}
                  className={`py-4 px-2 text-sm font-medium ${mode === 'break'
                    ? 'text-green-600 dark:text-green-400 border-b-2 border-green-500'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  Short Break
                </button>
                <button
                  onClick={() => switchMode('longBreak')}
                  className={`py-4 px-2 text-sm font-medium ${mode === 'longBreak'
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                  Long Break
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats and Instructions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Today's Progress
            </h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{totalWorkSessions}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Sessions</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Math.floor(totalWorkSessions * (WORK_DURATION / 60))}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Minutes</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Math.floor(totalWorkSessions / WORK_SESSIONS_BEFORE_LONG_BREAK)}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Long Breaks</div>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
              How to Use
            </h3>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-2">
              <li>• Click <span className="font-medium">Start</span> to begin your focus session</li>
              <li>• Work for 25 minutes (1 Pomodoro)</li>
              <li>• Take a 5-minute break</li>
              <li>• After 4 Pomodoros, take a longer 15-minute break</li>
              <li>• Use the buttons to switch between work and break modes</li>
            </ul>
          </div>
        </div>

        {/* Informational Content */}
        <div className="mt-12">
          <InformationalContent
            title={pomodoroTimerContent.title}
            description={pomodoroTimerContent.description}
            content={pomodoroTimerContent.content}
            faqs={pomodoroTimerContent.faqs}
            relatedTools={pomodoroTimerContent.relatedTools}
          />
        </div>
      </div>
    </div>
  );
}
